import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import AnimatedButton from '../AnimatedButton';
import type { CountryProps } from '../../pages/Cannabis';

/* ── Types ── */
interface Dispensary {
  id?: number | string;
  name: string;
  city?: string;
  country?: string;
  country_code?: string;
  type?: string;
  rating?: number;
  website?: string;
  description?: string;
  address?: string;
  phone?: string;
}

interface CountryGroup {
  country: string;
  code: string;
  count: number;
}

import { countryFlag } from '../../lib/country-utils';

function StarRating({ rating }: { rating: number }) {
  const stars = Math.round(rating);
  return (
    <span className="text-[#d4a574] text-xs">
      {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
      <span className="text-white/30 ml-1">{rating.toFixed(1)}</span>
    </span>
  );
}

function typeBadge(type?: string) {
  if (typeof type !== 'string') return 'bg-white/10 text-white/60 border-white/20';
  switch (type.toLowerCase()) {
    case 'recreational': return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 'medical': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 'delivery': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 'coffeeshop': return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

export default function DispensariesTab({ userCountry, isGlobal }: CountryProps) {
  const [countries, setCountries] = useState<CountryGroup[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [dispensaries, setDispensaries] = useState<Dispensary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch country list on mount
  useEffect(() => {
    async function fetchCountries() {
      try {
        // Get all dispensaries to extract country groups
        const res = await fetch(getApiUrl('/api/dispensaries-world?per_page=999'));
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        const items: Dispensary[] = data.items ?? [];

        // Group by country
        const map = new Map<string, { code: string; count: number }>();
        for (const d of items) {
          if (!d.country) continue;
          const existing = map.get(d.country);
          if (existing) {
            existing.count++;
          } else {
            map.set(d.country, { code: d.country_code || '', count: 1 });
          }
        }

        const groups: CountryGroup[] = Array.from(map.entries())
          .map(([country, { code, count }]) => ({ country, code, count }))
          .sort((a, b) => b.count - a.count);

        setCountries(groups);
      } catch {
        setCountries([]);
      } finally {
        setLoading(false);
      }
    }
    fetchCountries();
  }, []);

  // Auto-select user's country when not in global mode
  useEffect(() => {
    if (userCountry && !isGlobal && countries.length > 0 && !selectedCountry) {
      const match = countries.find(c => c.country === userCountry.name);
      if (match) setSelectedCountry(match.country);
    }
  }, [userCountry, isGlobal, countries, selectedCountry]);

  // Fetch dispensaries for selected country
  const fetchDispensaries = useCallback(async (pageNum: number, append: boolean) => {
    if (!selectedCountry) return;
    append ? setLoadingMore(true) : setLoadingList(true);
    try {
      const params = new URLSearchParams({
        page: String(pageNum),
        per_page: '30',
        country: selectedCountry,
      });
      if (search.trim()) params.set('search', search.trim());

      const res = await fetch(getApiUrl(`/api/dispensaries-world?${params}`));
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      const items: Dispensary[] = data.items ?? [];
      setTotalCount(data.total ?? items.length);
      setDispensaries(prev => append ? [...prev, ...items] : items);
      setHasMore(items.length >= 30);
    } catch {
      if (!append) setDispensaries([]);
    } finally {
      setLoadingList(false);
      setLoadingMore(false);
    }
  }, [selectedCountry, search]);

  useEffect(() => {
    if (selectedCountry) {
      setPage(1);
      fetchDispensaries(1, false);
    }
  }, [fetchDispensaries, selectedCountry]);

  function loadMore() {
    const next = page + 1;
    setPage(next);
    fetchDispensaries(next, true);
  }

  function goBack() {
    setSelectedCountry(null);
    setDispensaries([]);
    setSearch('');
    setPage(1);
  }

  // ── COUNTRY PICKER ──
  if (!selectedCountry) {
    if (loading) {
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[...Array(9)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      );
    }

    if (countries.length === 0) {
      return (
        <div className="text-center py-16 text-white/40">
          <p>No dispensaries available.</p>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <p className="text-white/50 text-sm">Select a country to browse dispensaries</p>
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-2 sm:grid-cols-3 gap-3"
        >
          {countries.map(c => (
            <motion.button
              key={c.country}
              variants={staggerItem}
              onClick={() => setSelectedCountry(c.country)}
              className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-left hover:border-[#4ade80]/30 hover:bg-white/[0.04] transition-all duration-200"
            >
              <div className="flex items-center gap-3">
                <span className="text-3xl">{countryFlag(c.code)}</span>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{c.country}</p>
                  <p className="text-white/40 text-xs">{c.count} {c.count === 1 ? 'dispensary' : 'dispensaries'}</p>
                </div>
              </div>
              <span className="absolute top-3 right-3 text-white/20 group-hover:text-white/50 transition-colors text-lg">→</span>
            </motion.button>
          ))}
        </motion.div>
      </div>
    );
  }

  // ── DISPENSARY LIST ──
  const selectedCountryData = countries.find(c => c.country === selectedCountry);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={goBack}
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors"
        >
          ←
        </button>
        <span className="text-2xl">{countryFlag(selectedCountryData?.code)}</span>
        <div>
          <h3 className="text-white font-semibold text-base">{selectedCountry}</h3>
          <p className="text-white/40 text-xs">{totalCount} {totalCount === 1 ? 'dispensary' : 'dispensaries'}</p>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder={`Search in ${selectedCountry}...`}
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#4ade80]/40 transition-colors"
      />

      {/* Loading / List */}
      {loadingList ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : dispensaries.length === 0 ? (
        <div className="text-center py-12 text-white/40">
          <p>No dispensaries found.</p>
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-3"
        >
          <AnimatePresence>
            {dispensaries.map((d, i) => (
              <motion.div
                key={d.id ? String(d.id) : `${d.name}-${i}`}
                variants={staggerItem}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-white/20 transition-colors"
              >
                <div className="space-y-2.5">
                  {/* Name + Type + Rating row */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-white font-semibold text-sm">{d.name}</h4>
                        {d.type && (
                          <span className={`shrink-0 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${typeBadge(d.type)}`}>
                            {d.type}
                          </span>
                        )}
                      </div>
                      {d.rating != null && d.rating > 0 && <StarRating rating={d.rating} />}
                    </div>
                    {/* Actions */}
                    <div className="flex gap-2 shrink-0">
                      {d.website && (
                        <a
                          href={d.website.startsWith('http') ? d.website : `https://${d.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[#4ade80]/30 text-[#4ade80] hover:bg-[#4ade80]/10 transition-colors"
                        >
                          Visit
                        </a>
                      )}
                      {d.phone && (
                        <a
                          href={`tel:${d.phone}`}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-white/20 text-white/60 hover:bg-white/5 transition-colors"
                        >
                          Call
                        </a>
                      )}
                    </div>
                  </div>

                  {/* City */}
                  {d.city && (
                    <p className="text-white/50 text-xs flex items-center gap-1">
                      {d.city}
                    </p>
                  )}

                  {/* Address */}
                  {d.address && (
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(d.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-white/35 text-xs hover:text-white/60 transition-colors"
                    >
                      {d.address}
                    </a>
                  )}

                  {/* Description */}
                  {d.description && (
                    <p className="text-white/40 text-xs leading-relaxed">{d.description}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Load More */}
      {!loadingList && hasMore && dispensaries.length > 0 && (
        <div className="flex justify-center pt-4">
          <AnimatedButton onClick={loadMore} loading={loadingMore} variant="secondary">
            Load More
          </AnimatedButton>
        </div>
      )}
    </div>
  );
}
